import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { ApiRequestError, type KenoBetLimit, type KenoPartnerLimits } from '@/lib/api'
import { useT } from '@/contexts/language'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Which KENO bet-limit rows one partner offers: a checklist of what its parent allows, the
 * rows its members really see today, and a warning when the choice leaves them with none.
 *
 * Used twice — by the admin for a master, and by a master for each of its agents. The
 * caller supplies `load` and `save`; this component knows nothing about who is asking, the
 * server decides what may be ticked.
 */

const fmt = (n: number) => n.toLocaleString('en-US')

export function rangeText(l: Pick<KenoBetLimit, 'min' | 'max'>): string {
  return `${fmt(l.min)} – ${fmt(l.max)}`
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: KenoPartnerLimits }

export function KenoLimitPicker({ load, save, canEdit }: {
  load: () => Promise<KenoPartnerLimits>
  save: (limitIds: string[]) => Promise<KenoPartnerLimits>
  canEdit: boolean
}) {
  const t = useT().kenoLimits
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const [draft, setDraft] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const apply = useCallback((data: KenoPartnerLimits) => {
    setState({ status: 'ok', data })
    setDraft(data.chosen)
  }, [])

  const reload = useCallback(async () => {
    setState({ status: 'loading' })
    setNotice(null)
    setSaveError(null)
    try {
      apply(await load())
    } catch (err) {
      setState({ status: 'error', message: err instanceof ApiRequestError ? err.message : t.loadFailed })
    }
  }, [load, apply, t.loadFailed])

  useEffect(() => { void reload() }, [reload])

  const submit = async (ids: string[], doneText: string) => {
    setSaving(true)
    setNotice(null)
    setSaveError(null)
    try {
      apply(await save(ids))
      setNotice(doneText)
    } catch (err) {
      setSaveError(err instanceof ApiRequestError ? err.message : t.saveFailed)
    } finally {
      setSaving(false)
    }
  }

  if (state.status === 'loading') {
    return (
      <div className="flex flex-col gap-2" aria-busy="true">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-2/3" />
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="flex items-center gap-3" role="alert">
        <AlertCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden />
        <p className="font-mono text-xs text-destructive">{state.message}</p>
        <Button variant="outline" size="sm" className="ml-auto h-8 font-mono text-xs" onClick={() => void reload()}>
          {t.reload}
        </Button>
      </div>
    )
  }

  const { data } = state
  const dirty = draft.length !== data.chosen.length || draft.some((id) => !data.chosen.includes(id))
  const toggle = (id: string) =>
    setDraft((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">{t.partnerHint}</p>

      {data.options.length === 0 ? (
        <p className="font-mono text-sm text-muted-foreground py-2">{t.optionsEmpty}</p>
      ) : (
        <fieldset className="flex flex-col gap-1.5" disabled={!canEdit || saving}>
          <legend className="sr-only">{t.partnerTitle}</legend>
          {data.options.map((o) => (
            <label
              key={o.id}
              className="flex items-center gap-3 rounded-[var(--radius)] border border-border bg-background px-3 py-2 min-h-11 cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={draft.includes(o.id)}
                onChange={() => toggle(o.id)}
              />
              <span className="font-mono text-sm tabular-nums">{rangeText(o)}</span>
            </label>
          ))}
        </fieldset>
      )}

      <div className="flex flex-col gap-1.5">
        <p className="text-xs text-muted-foreground">{t.membersSee}</p>
        <div className="flex flex-wrap gap-1.5">
          {data.effective.length === 0
            ? <Badge variant="destructive">—</Badge>
            : data.effective.map((l) => <Badge key={l.id} variant="outline" className="font-mono tabular-nums">{rangeText(l)}</Badge>)}
        </div>
        {data.chosen.length === 0 && !data.blocked && (
          <p className="text-xs text-muted-foreground">{t.nothingChosen}</p>
        )}
      </div>

      {data.blocked && (
        <div className="flex items-start gap-2 rounded-[var(--radius)] border border-destructive/40 bg-card p-3" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm">{t.blocked}</p>
        </div>
      )}

      {saveError && (
        <div className="flex items-center gap-2" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden />
          <p className="font-mono text-xs text-destructive">{saveError}</p>
        </div>
      )}
      {notice && (
        <div className="flex items-center gap-2" role="status">
          <CheckCircle2 className="h-4 w-4 text-success shrink-0" aria-hidden />
          <p className="text-sm">{notice}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="h-9" disabled={saving || !dirty} onClick={() => void submit(draft, t.saved)}>
            {t.save}
          </Button>
          {data.chosen.length > 0 && (
            <Button variant="ghost" size="sm" className="h-9" disabled={saving} onClick={() => void submit([], t.cleared)}>
              {t.clear}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
